import React, { useState, useEffect, useCallback } from 'react';
import { func } from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import BackButton from '../BackButton';
import SurveyParticipantsEditor from '../SurveyParticipantsEditor';
import {
  getSurveyParticipants,
  getSurveyInfo,
  getCourseUnitRealisations
} from '../../api';
import {
  addErrorMessageAction,
  setTaskFulfilled,
  setTaskPending,
  setSurveyParticipantsAction,
  initSurveyParticipantsAction
} from '../../actions';
import sharedStyles from '../../styles/shared';
import { withLang } from '../../i18n';
import useTitle from '../../hooks/useTitle';

const AddSurveyParticipants = ({ translate: t }) => {
  const dispatch = useDispatch();
  const { surveyId } = useParams();

  const participants = useSelector((state) => state.survey.surveyOptions.participants);
  const courses = useSelector((state) => state.survey.surveyOptions.courses);

  const [submitted, setSubmitted] = useState(false);
  const [surveyInfo, setSurveyInfo] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useTitle(t('addSurveyParticipants.title'));

  useEffect(() => {
    const fetchData = async () => {
      try {
        const surveyInfoData = await getSurveyInfo(surveyId);
        setSurveyInfo(surveyInfoData);
        const courseIds = surveyInfoData.courseRealisationIds;
        dispatch(setTaskPending());
        const [participantsData, coursesData] = await Promise.all([
          getSurveyParticipants(surveyId),
          courseIds?.length ? getCourseUnitRealisations(courseIds, surveyId) : []
        ]);
        dispatch(setTaskFulfilled());
        dispatch(
          initSurveyParticipantsAction({
            participants: participantsData,
            courses: coursesData,
            onlyManuallyAdded: true
          })
        );
        setLoaded(true);
      } catch (err) {
        dispatch(addErrorMessageAction(err));
      }
    };

    fetchData();
  }, [surveyId, dispatch]);

  const onSave = useCallback(async () => {
    await dispatch(
      setSurveyParticipantsAction(surveyId, {
        participants,
        courseRealisationIds: courses.map((course) => course.id)
      })
    );
    setSubmitted(true);
  }, [dispatch, surveyId, participants, courses]);

  const newParticipants = (participants || []).filter((participant) => participant.new);
  const hasNoInvalidParticipants = newParticipants.every((participant) => !participant.error);

  return (
    <div className="page-container">
      <BackButton />
      {!submitted ? (
        <div>
          <h2 className="panel__title">{t('addSurveyParticipants.title')}</h2>
          {surveyInfo && loaded && (
            <SurveyParticipantsEditor
              surveyId={surveyId}
              showAsRequired={false}
              surveyType={surveyInfo.type}
            />
          )}
          <button
            type="button"
            disabled={!hasNoInvalidParticipants}
            className={`button ${sharedStyles.submitButton} ${sharedStyles.separator}`}
            onClick={onSave}
            data-cy="survey-participants-edit-save-button"
          >
            {t('common.save')}
          </button>
        </div>
      ) : (
        <h2 className="panel__title">{t('addSurveyParticipants.saved')}</h2>
      )}
    </div>
  );
};

AddSurveyParticipants.propTypes = {
  translate: func.isRequired
};

export default withLang(AddSurveyParticipants);
